import React, { useState } from 'react';
import { useFrappePostCall } from 'frappe-react-sdk';

interface ClassificationResult {
  suggested_color: string;
  confidence: number;
  reasoning: string;
}

interface UseSimpleLabelClassifierReturn {
  suggestedColor: ClassificationResult | null;
  isClassifying: boolean;
  error: string | null;
  classifyComment: (commentText: string, videoTitle?: string, sessionName?: string) => Promise<string | null>;
  clearSuggestion: () => void;
  aiEnabled: boolean;
  toggleAI: () => Promise<void>;
  isTogglingAI: boolean;
}

export const useSimpleLabelClassifier = (): UseSimpleLabelClassifierReturn => {
  const [suggestedColor, setSuggestedColor] = useState<ClassificationResult | null>(null);
  const [isClassifying, setIsClassifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [isTogglingAI, setIsTogglingAI] = useState(false);

  // API calls
  const { call: callClassifier } = useFrappePostCall('surgical_training.api.simple_label_classifier.classify_comment_label');
  const { call: callGetPreference } = useFrappePostCall('surgical_training.api.simple_label_classifier.get_user_ai_preference');
  const { call: callSetPreference } = useFrappePostCall('surgical_training.api.simple_label_classifier.set_user_ai_preference');

  const classifyComment = async (commentText: string, videoTitle = '', sessionName = ''): Promise<string | null> => {
    console.log('🤖 Classification Debug: Starting classification', {
      aiEnabled,
      commentLength: commentText.trim().length,
      videoTitle: videoTitle || 'none',
      sessionName: sessionName || 'none'
    });

    if (!aiEnabled) {
      console.log('🤖 Classification Debug: AI disabled, skipping');
      return null;
    }

    if (!commentText.trim() || commentText.trim().length < 10) {
      console.log('🤖 Classification Debug: Comment too short');
      setError('Comment too short for classification');
      return null;
    }

    try {
      console.log('🤖 Classification Debug: Calling API');
      setIsClassifying(true);
      setError(null);

      const response = await callClassifier({
        comment_text: commentText,
        video_title: videoTitle,
        session_name: sessionName
      });

      console.log('🤖 Classification Debug: Raw API response', response);
      console.log('🤖 Classification Debug: Response analysis', {
        responseType: typeof response,
        responseKeys: response ? Object.keys(response) : 'null',
        message: response?.message,
        success: response?.message === 'Success',
        color: response?.suggested_color,
        confidence: response?.confidence,
        error: response?.error
      });

      // The useFrappePostCall wraps the API response in a 'message' property
      const apiResponse = response?.message || response;

      console.log('🤖 Classification Debug: API response extracted', apiResponse);

      if (apiResponse?.message === 'Success') {
        setSuggestedColor({
          suggested_color: apiResponse.suggested_color,
          confidence: apiResponse.confidence,
          reasoning: apiResponse.reasoning
        });
        console.log('🤖 Classification Debug: Success, color set', {
          color: apiResponse.suggested_color,
          confidence: apiResponse.confidence
        });
        return apiResponse.suggested_color; // Return the suggested color
      } else {
        const errorMsg = apiResponse?.error || 'Classification failed';
        setError(errorMsg);
        setSuggestedColor(null);
        console.log('🤖 Classification Debug: Failed', errorMsg);
        return null;
      }
    } catch (err: any) {
      console.log('🤖 Classification Debug: Exception', err.message);
      setError(err.message || 'Classification failed');
      setSuggestedColor(null);
      return null;
    } finally {
      setIsClassifying(false);
      console.log('🤖 Classification Debug: Complete');
    }
  };

  const clearSuggestion = () => {
    setSuggestedColor(null);
    setError(null);
  };

  const toggleAI = async () => {
    try {
      setIsTogglingAI(true);
      console.log('🤖 Toggle Debug: Starting toggle, current aiEnabled:', aiEnabled);
      
      const response = await callSetPreference({
        enabled: !aiEnabled
      });

      console.log('🤖 Toggle Debug: API response:', response);

      // Check for nested response structure like the classification API
      const apiResponse = response?.message || response;
      console.log('🤖 Toggle Debug: Extracted response:', apiResponse);

      if (apiResponse?.message === 'Success') {
        setAiEnabled(apiResponse.ai_enabled);
        console.log('🤖 Toggle Debug: Success, new aiEnabled:', apiResponse.ai_enabled);
        if (!apiResponse.ai_enabled) {
          clearSuggestion(); // Clear any existing suggestions when disabled
        }
      } else {
        console.log('🤖 Toggle Debug: Failed to toggle, response not success');
      }
    } catch (err: any) {
      console.log('🤖 Toggle Debug: Exception:', err);
    } finally {
      setIsTogglingAI(false);
      console.log('🤖 Toggle Debug: Toggle completed');
    }
  };

  // Load user preference on mount
  React.useEffect(() => {
    const loadPreference = async () => {
      try {
        const response = await callGetPreference({});

        // Check for nested response structure
        const apiResponse = response?.message || response;

        const aiEnabledValue = apiResponse.ai_enabled !== false; // Default to true
        setAiEnabled(aiEnabledValue);
        } catch (err) {
        setAiEnabled(true); // Default to enabled
      }
    };

    loadPreference();
  }, [callGetPreference]);

  return {
    suggestedColor,
    isClassifying,
    error,
    classifyComment,
    clearSuggestion,
    aiEnabled,
    toggleAI,
    isTogglingAI
  };
};